// src/features/admin/MigrationPanel.jsx
//
// Slice 11b: admin tool for the localStorage→Postgres cutover.
//
// Two-step flow on purpose:
//   1. Validate  — parse JSON, normalize for schema, surface counts + issues.
//                  Run-migration stays disabled until this passes.
//   2. Run       — POST the normalized payload to /api/bulk-import.
//
// Critical UX rule (from the slice brief): never silently overwrite. If
// the server returns 409 (collision with existing location ids), the
// banner surfaces the full conflicting-id list verbatim and tells the
// admin to dedupe locally first. We don't auto-resolve, don't upsert,
// and don't drop the conflicting records — the admin owns the decision.
//
// Every action emits a log.audit envelope so the admin's intent is
// captured even when the request never reaches the server (e.g. picked
// a malformed file, validation failed).

import React, { useRef, useState } from "react";
import { bulkImport } from "../../data/operationsClient.js";
import { ConflictError, ValidationError } from "../../data/errors.js";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { DataTabHeading } from "../../components/DataTabHeading.jsx";
import { log } from "../../utils/logger.js";
import { parseLegacyJson, validateForImport } from "./migrationParser.js";

// Slice Adm2 (2026-05-12) — admin-section button convention.
//
// This file + ExportPanel.jsx + FxRatesPanel.jsx are the three admin
// panels that share a refreshed visual language. The convention is:
//
//   - Section-level action buttons (top-of-panel CTAs that drive the
//     panel's primary flow): padding "7px 14px".
//   - Row-level action buttons (inline edit/save/cancel inside table
//     rows, e.g. FxRatesPanel.EDIT_BUTTON): padding "4px 12px" — the
//     tighter rhythm fits a table cell.
//
//   - PRIMARY (filled blue):  T.blue bg, T.white text, no border,
//                              6px radius, 12px / 700.
//   - SECONDARY (outlined):    T.card bg, T.dark text,
//                              0.5px solid T.border, 6px radius,
//                              12px / 600.
//
// `S6.5` visual-hierarchy logic in this file (Validate vs Run prominence
// flip based on validation state and busy state) is unchanged. The
// refresh is purely about the *style values* on PRIMARY/SECONDARY, not
// about which button gets which role at which moment.
const PRIMARY_BUTTON = {
  background: T.blue, color: T.white, border: "none", borderRadius: 6,
  fontSize: T.fsSm, fontWeight: T.fwBold, padding: "7px 14px", cursor: "pointer",
};
const SECONDARY_BUTTON = {
  background: T.card, color: T.dark, border: `0.5px solid ${T.border}`,
  borderRadius: 6, fontSize: T.fsSm, fontWeight: T.fwSemibold, padding: "7px 14px",
  cursor: "pointer",
};

const COUNT_LABELS = {
  locations:     "Locations",
  leases:        "Leases",
  allowances:    "Allowances",
  draws:         "Allowance draws",
  criticalDates: "Critical dates",
  watchItems:    "Watch items",
  projects:      "Projects",
  candidates:    "Candidates",
  commentary:    "Commentary",
  utilization:   "Utilization rows",
  headcount:     "Headcount snapshots",
  locationCosts: "Location-cost rows",
};

export function MigrationPanel({ mode = "import" }) {
  const isRestore = mode === "restore";
  const headingText   = isRestore ? "Restore from JSON" : "Import from JSON";
  const descriptionText = isRestore
    ? "Restore a previously exported portfolio JSON backup. Validate first to preview row counts; only then is Run restore enabled. Strict insert - if any id already exists the whole restore is rolled back."
    : "Bulk-import a JSON portfolio (locations + nested data). Paste or upload a JSON file. Validate first to preview row counts and surface any issues; only then is Run import enabled. Strict insert - if any id already exists in the database the whole import is rolled back and you'll see the conflicting ids.";
  const [text,  setText]  = useState("");
  const [validation, setValidation] = useState(null); // { ok, issues, normalized, counts, locationIds }
  const [error, setError] = useState(null);
  const [busy,  setBusy]  = useState(false);
  const [result, setResult] = useState(null); // { migrated, locationIds, inserted } from server
  const fileRef = useRef(null);

  const onPickFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.json$/i.test(file.name)) {
      setError(new ValidationError(`Expected a .json file; got "${file.name}".`, { field: "file" }));
      return;
    }
    try {
      const body = await file.text();
      setText(body);
      setError(null);
      setValidation(null);
      setResult(null);
      log.audit("migration.file_loaded", { name: file.name, size: file.size });
    } catch (err) {
      setError(err);
    }
  };

  const onValidate = () => {
    setError(null);
    setResult(null);
    log.audit("migration.validate_clicked", { bytes: text.length });
    let raw;
    try {
      raw = parseLegacyJson(text);
    } catch (err) {
      setValidation(null);
      setError(new ValidationError(err.message, { field: "json" }));
      return;
    }
    const v = validateForImport(raw);
    setValidation(v);
    log.audit("migration.validated", {
      ok: v.ok,
      locations: v.counts.locations,
      issues: v.issues.length,
    });
  };

  const onRun = async () => {
    if (!validation || !validation.ok) return;
    setBusy(true);
    setError(null);
    setResult(null);
    log.audit("migration.run_clicked", {
      locations: validation.counts.locations,
      ids: validation.locationIds,
    });
    try {
      const out = await bulkImport(validation.normalized);
      setResult(out);
      log.audit("migration.complete", { migrated: out.migrated });
    } catch (err) {
      // ConflictError carries the verbatim server message
      // ("Duplicate primary key: AMER-SFO-01") — surface as-is plus the
      // explicit "no auto-resolve" guidance the brief calls out.
      setError(err);
      log.audit("migration.failed", { name: err?.name, msg: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const onReset = () => {
    setText("");
    setValidation(null);
    setError(null);
    setResult(null);
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  const v = validation;

  return (
    // Slice Adm1 (2026-05-12): added `data-testid="migration-panel"` on the
    // outer wrapper so e2e specs can assert the section is mounted after
    // clicking the sub-sidebar's "Data import" item. No behavior change —
    // this is the only edit MigrationPanel takes for the relocation.
    <div data-testid="migration-panel">
      <DataTabHeading>{headingText}</DataTabHeading>
      <div style={{ fontSize: T.fsSm, color: T.mid, marginBottom: 20, lineHeight: 1.5 }}>
        {descriptionText}
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button onClick={onPickFile} type="button" style={SECONDARY_BUTTON}>
          Choose .json file…
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          onChange={onFile}
          style={{ display: "none" }}
          data-testid="migration-file-input"
        />
        <button onClick={onReset} type="button" style={SECONDARY_BUTTON}>Reset</button>
      </div>

      <textarea
        value={text}
        onChange={(e) => { setText(e.target.value); setValidation(null); setResult(null); }}
        rows={10}
        placeholder='Paste JSON: an array of locations, or { "locations": [...] }'
        style={{
          width: "100%", padding: "12px 14px", fontSize: T.fsSm,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          border: `0.5px solid ${T.border}`, borderRadius: 6,
          color: T.dark, background: T.white, outline: "none",
          resize: "vertical", boxSizing: "border-box",
          marginBottom: 18,
        }}
        data-testid="migration-textarea"
      />

      {/*
        S6.5 — visual hierarchy follows "the next thing to do":
        before validation passes, Validate is the prominent filled
        button and Run is muted/disabled; once validation passes,
        Run becomes prominent and Validate fades to secondary
        (re-validate stays available for textarea edits). Run's
        disabled-state logic is unchanged.
      */}
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        {(() => {
          const validatePassed = !!v?.ok;
          const validateDisabled = !text.trim() || busy;
          const runDisabled = !validatePassed || busy;
          // Prominence rule (S6.5 + architect follow-up): the prominent
          // button is always the *next clickable action*. While `busy`
          // (an import is in flight) Run is disabled, so we de-emphasize
          // both buttons rather than leaving Run filled-blue-but-dead.
          const runProminent = validatePassed && !busy;
          const validateBase = runProminent ? SECONDARY_BUTTON : PRIMARY_BUTTON;
          const runBase      = runProminent ? PRIMARY_BUTTON   : SECONDARY_BUTTON;
          return (
            <>
              <button
                onClick={onValidate}
                type="button"
                disabled={validateDisabled}
                data-testid="migration-validate"
                style={{
                  ...validateBase,
                  opacity: validateDisabled ? 0.5 : 1,
                  cursor: validateDisabled ? "default" : "pointer",
                }}
              >
                {validatePassed ? "Re-validate" : "Validate"}
              </button>
              <button
                onClick={onRun}
                type="button"
                disabled={runDisabled}
                data-testid="migration-run"
                style={{
                  ...runBase,
                  opacity: runDisabled ? 0.5 : 1,
                  cursor: runDisabled ? "default" : "pointer",
                }}
              >
                {busy ? (isRestore ? "Restoring…" : "Importing…") : (isRestore ? "Run restore" : "Run import")}
              </button>
            </>
          );
        })()}
      </div>

      {/* Validation report */}
      {v && (
        <div data-testid="migration-validation" style={{
          background: T.bg,
          border: `1px solid ${T.border}`,
          borderRadius: 8,
          padding: 12,
          marginBottom: 12,
        }}>
          <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark, marginBottom: 8 }}>
            Validation - {v.ok ? (isRestore ? "ready to restore" : "ready to import") : "blocked"}
          </div>
          <CountsGrid counts={v.counts} />
          {v.locationIds.length > 0 && (
            <details style={{ marginTop: 10, fontSize: T.fsSm, color: T.mid }}>
              <summary style={{ cursor: "pointer", fontWeight: T.fwSemibold, color: T.dark }}>
                {v.locationIds.length} location id(s) will be created
              </summary>
              <div style={{
                marginTop: 6, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: T.fsXs, color: T.dark, lineHeight: 1.6, wordBreak: "break-all",
              }}>
                {v.locationIds.join(", ")}
              </div>
            </details>
          )}
          {v.issues.length > 0 && <IssueList issues={v.issues} />}
        </div>
      )}

      {/* 409 collision detail. ConflictError.details.conflicts may be
          provided by the server; if not, fall back to the message. */}
      {error instanceof ConflictError && (
        <ConflictDetail err={error} />
      )}

      {/* Success card */}
      {result && (
        <div data-testid="migration-result" style={{
          background: T.greenSoft,
          border: `1px solid ${T.successBorder}`,
          color: T.successText,
          borderRadius: 8,
          padding: 12,
          fontSize: T.fsBase,
        }}>
          <div style={{ fontWeight: T.fwBold, marginBottom: 6 }}>
            {isRestore ? "Restored" : "Imported"} {result.migrated} location(s).
          </div>
          <div style={{ fontSize: T.fsXs, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: T.successText }}>
            {(result.locationIds || []).join(", ")}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────

function CountsGrid({ counts }) {
  const rows = Object.entries(COUNT_LABELS)
    .map(([k, label]) => [label, counts[k] || 0])
    .filter(([, n]) => n > 0);
  if (rows.length === 0) return <div style={{ fontSize: T.fsSm, color: T.light }}>No records to import.</div>;
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: "4px 16px",
    }}>
      {rows.map(([label, n]) => (
        <div key={label} style={{ fontSize: T.fsSm, color: T.dark }}>
          <span style={{ color: T.mid }}>{label}:</span> <strong>{n}</strong>
        </div>
      ))}
    </div>
  );
}

function IssueList({ issues }) {
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>
        Issues ({issues.length})
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: T.fsXs, color: T.dark, lineHeight: 1.55 }}>
        {issues.slice(0, 50).map((iss, i) => (
          <li key={i} style={{ color: iss.severity === "error" ? T.red : T.mid }}>
            <strong>{iss.severity}</strong>
            {iss.id ? ` [${iss.id}]` : ""}: {iss.message}
          </li>
        ))}
        {issues.length > 50 && (
          <li style={{ color: T.light, fontStyle: "italic" }}>
            …and {issues.length - 50} more (resolve the listed issues first).
          </li>
        )}
      </ul>
    </div>
  );
}

function ConflictDetail({ err }) {
  // The server (server/lib/errors.js → mapPgError on 23505) packs the
  // duplicate id list into err.details.conflicts when it can isolate it.
  // Fall back to the raw message for other 409 shapes.
  const conflicts = Array.isArray(err?.details?.conflicts) ? err.details.conflicts : null;
  return (
    <div style={{
      background: T.redSoft,
      border: `1px solid ${T.redRing}`,
      color: T.red,
      borderRadius: 8,
      padding: 12,
      marginBottom: 12,
      fontSize: T.fsBase,
    }}>
      <div style={{ fontWeight: T.fwBold, marginBottom: 6 }}>Import rolled back: id collision</div>
      {conflicts ? (
        <>
          <div style={{ marginBottom: 6 }}>
            The following id(s) already exist in the database. Delete them
            (or remove them from your JSON) and re-validate before retrying.
            We do not auto-overwrite or auto-skip:
          </div>
          <div style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: T.fsXs, wordBreak: "break-all",
          }}>
            {conflicts.join(", ")}
          </div>
        </>
      ) : (
        <div>{err.message}</div>
      )}
    </div>
  );
}
